// Icônes des réseaux sociaux dans les signatures.
//
// Contrainte dure : PNG obligatoire (le moteur Word d'Outlook Windows ne rend pas le SVG, Gmail ne
// l'accepte qu'inline avec restrictions). Sources : PNG noirs 48×48 sur fond transparent, officiels,
// servis avec le back-office sous /icons/<réseau>.png — jamais redessinés. Affichage 24×24 avec
// width/height en ATTRIBUTS HTML (Outlook réserve la place).
//
// Un PNG ne se recolore pas en CSS dans un mail. Si social_color n'est pas noir, une variante
// teintée est générée à l'enregistrement de la charte (web/src/tint.ts) et stockée dans le bucket
// sous icons/<brand_id>-<réseau>-<hash8>.png ; l'URL en dérive de façon déterministe, ici, pour que
// le rendu (navigateur ET Edge Functions) la retrouve sans lire le bucket. Noir = asset commun,
// une seule URL en cache pour toutes les chartes, aucune génération.
import type { Brand, SocialNetworkId } from './types.ts';
import { hash8 } from './util.ts';

export type SocialNetwork = SocialNetworkId;

export interface SocialLink {
  id: SocialNetwork;
  label: string; // alt de l'icône : Outlook ancien et images bloquées doivent rester lisibles
  field: 'website_url' | 'instagram_url' | 'facebook_url' | 'tiktok_url' | 'linkedin_url';
}

/** Ordre d'affichage : l'icône « web » (site du lieu) d'abord, puis les réseaux. */
export const SOCIAL_NETWORKS: readonly SocialLink[] = [
  { id: 'web', label: 'Site web', field: 'website_url' },
  { id: 'instagram', label: 'Instagram', field: 'instagram_url' },
  { id: 'facebook', label: 'Facebook', field: 'facebook_url' },
  { id: 'tiktok', label: 'TikTok', field: 'tiktok_url' },
  { id: 'linkedin', label: 'LinkedIn', field: 'linkedin_url' },
];

/** Taille du fichier source (px) et taille d'affichage (px) : export 2× pour les écrans Retina. */
export const ICON_SOURCE_SIZE = 48;
export const ICON_DISPLAY_SIZE = 24;
/** À incrémenter si les PNG sources changent : toutes les URL teintées changent avec. */
export const ICON_SET_VERSION = 1;

export const LINKEDIN_COLOR_NOTE =
  'LinkedIn n’autorise son logo qu’en bleu, noir ou blanc. Une autre teinte sort de ses règles d’usage.';

export function normalizeHex(color: string | null | undefined): string {
  const v = (color ?? '').trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(v)) return '#' + [...v.slice(1)].map((c) => c + c).join('');
  return /^#[0-9a-f]{6}$/.test(v) ? v : '#000000';
}

export function isBlack(color: string | null | undefined): boolean {
  return normalizeHex(color) === '#000000';
}

/** Suffixe de l'objet teinté : dépend du jeu d'icônes, du réseau et de la teinte, jamais de la date. */
export function tintedIconHash(network: SocialNetwork, color: string): string {
  return hash8(`${ICON_SET_VERSION}:${network}:${normalizeHex(color)}`);
}

/** Chemin dans le bucket signature-assets de la variante teintée. */
export function tintedIconPath(brandId: string, network: SocialNetwork, color: string): string {
  return `icons/${brandId}-${network}-${tintedIconHash(network, color)}.png`;
}

/** URL de l'icône à mettre dans la signature : asset commun si noir, variante teintée sinon. */
export function socialIconUrl(base: string, brand: Pick<Brand, 'id' | 'social_color'>, network: SocialNetwork, color: string = brand.social_color): string {
  const root = base.replace(/\/$/, '');
  if (isBlack(color)) return `${root}/icons/${network}.png`;
  return `${root}/a/${tintedIconPath(brand.id, network, color)}`;
}

/**
 * Icônes à afficher, dans l'ordre du jeu (« web » d'abord, vers le site) : chaque réseau dont
 * l'adresse est renseignée sur la charte — jamais d'icône morte —, restreint à `wanted` si le bloc
 * de mise en page en a retiré (l'icône web est présente par défaut, retirable dans l'éditeur).
 */
export function socialLinks(brand: Brand, wanted?: readonly SocialNetwork[]): { network: SocialLink; url: string }[] {
  return SOCIAL_NETWORKS.flatMap((network) => {
    if (wanted && !wanted.includes(network.id)) return [];
    const url = brand[network.field];
    return url ? [{ network, url }] : [];
  });
}
